// src/features/dashboard/components/EmployeeLoggedHoursReport/HourlyActivityLog.tsx

import {
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Rectangle,
} from 'recharts';

import {
  HourlyLoggedResponse,
  LoggedHoursKpiResponse,
} from '../../types/Employee/employeeLoggedHours.types';

interface Props {
  data?: HourlyLoggedResponse | null;
  kpi?: LoggedHoursKpiResponse | null;
}

type ActivityType =
  | 'active'
  | 'meeting'
  | 'idle';

interface ChartItem {
  hour: number;
  time: string;
  active: number;
  meeting: number;
  idle: number;
}

/* ==========================================================================
   COLORS
========================================================================== */

const ACTIVITY_COLORS = {
  active: '#4CC66F',
  meeting: '#5A8DEE',
  idle: '#F3AD2B',
};

/* ==========================================================================
   HELPERS
========================================================================== */

const formatTime = (
  time?: string | null,
): string => {
  if (!time) {
    return '--:--';
  }

  const parts =
    time.split(':');

  if (parts.length < 2) {
    return time;
  }

  return `${parts[0].padStart(
    2,
    '0',
  )}:${parts[1].padStart(
    2,
    '0',
  )}`;
};

const timeToMinutes = (
  time?: string | null,
): number | null => {
  if (!time) {
    return null;
  }

  const parts =
    time.split(':');

  if (parts.length < 2) {
    return null;
  }

  const hours =
    Number(parts[0]);

  const minutes =
    Number(parts[1]);

  if (
    Number.isNaN(hours) ||
    Number.isNaN(minutes)
  ) {
    return null;
  }

  return (
    hours * 60 +
    minutes
  );
};

const getCurrentMinutes =
  (): number => {
    const now =
      new Date();

    return (
      now.getHours() * 60 +
      now.getMinutes()
    );
  };

const summaryValue = (
  value?: string | null,
): string => {
  return value || '--';
};

/* ==========================================================================
   HOUR RANGE

   05 -> 05:00 - 06:00
   06 -> 06:00 - 07:00
   07 -> 07:00 - 08:00
   08 -> 08:00 - 09:00
   09 -> 09:00 - 10:00
   10 -> 10:00 - 11:00
========================================================================== */

const formatHourRange = (
  value?:
    | string
    | number
    | null,
): string => {
  if (
    value === undefined ||
    value === null ||
    value === ''
  ) {
    return '--:-- - --:--';
  }

  const rawValue =
    String(value).trim();

  let hour: number;

  if (
    rawValue.includes(':')
  ) {
    hour = Number(
      rawValue.split(':')[0],
    );
  } else {
    hour = Number(
      rawValue,
    );
  }

  if (
    !Number.isFinite(hour)
  ) {
    return '--:-- - --:--';
  }

  hour = Math.max(
    0,
    Math.min(
      23,
      Math.floor(hour),
    ),
  );

  const nextHour =
    hour === 23
      ? 24
      : hour + 1;

  const startTime =
    `${hour
      .toString()
      .padStart(
        2,
        '0',
      )}:00`;

  const endTime =
    `${nextHour
      .toString()
      .padStart(
        2,
        '0',
      )}:00`;

  return `${startTime} - ${endTime}`;
};

/* ==========================================================================
   TOP-ROUNDED BAR SHAPE

   IMPORTANT:

   Recharts stacked bars are separate rectangles.

   We want:

       ┌───────┐
       │ Idle  │  <- rounded only at top
       ├───────┤
       │Meeting│  <- square
       ├───────┤
       │Active │  <- square
       └───────┘

   If Active is the top segment:

       ┌───────┐
       │Active │  <- rounded
       └───────┘
========================================================================== */

const TopRoundedBar =
  (props: any) => {
    const {
      x,
      y,
      width,
      height,
      fill,
      payload,
      dataKey,
    } = props;

    if (
      width === undefined ||
      height === undefined ||
      x === undefined ||
      y === undefined
    ) {
      return null;
    }

    /*
     * Determine which activity
     * is the last non-zero segment.
     *
     * That segment receives the
     * rounded top.
     */

    const active =
      Number(
        payload?.active ?? 0,
      );

    const meeting =
      Number(
        payload?.meeting ?? 0,
      );

    const idle =
      Number(
        payload?.idle ?? 0,
      );

    let topActivity:
      | ActivityType
      | null = null;

    if (idle > 0) {
      topActivity = 'idle';
    } else if (meeting > 0) {
      topActivity = 'meeting';
    } else if (active > 0) {
      topActivity = 'active';
    }

    /*
     * Convert Recharts dataKey
     * into our activity type.
     */

    const currentActivity =
      dataKey as ActivityType;

    const isTopSegment =
      currentActivity ===
      topActivity;

    /*
     * Small values can create
     * extremely thin rectangles.
     *
     * Keep the height exactly as
     * Recharts calculates it.
     */

    if (height <= 0) {
      return null;
    }

    /*
     * Only the actual top segment
     * gets rounded.
     *
     * Bottom corners remain square.
     */

    const radius =
      isTopSegment
        ? [
            4,
            4,
            0,
            0,
          ]
        : [
            0,
            0,
            0,
            0,
          ];

    return (
      <Rectangle
        {...props}
        x={x}
        y={y}
        width={width}
        height={height}
        fill={fill}
        radius={radius}
      />
    );
  };

/* ==========================================================================
   COMPONENT
========================================================================== */

const HourlyActivityLog = ({
  data,
  kpi,
}: Props) => {
  /* ========================================================================
     CURRENT TIME
  ======================================================================== */

  const [
    currentMinutes,
    setCurrentMinutes,
  ] = useState(
    getCurrentMinutes(),
  );

  /* ========================================================================
     LEGEND
  ======================================================================== */

  const [
    activeLegend,
    setActiveLegend,
  ] = useState<ActivityType | null>(
    null,
  );

  /* Keep the last-bar tooltip inside the chart area. */
  const [
    tooltipPosition,
    setTooltipPosition,
  ] = useState<
    {
      x?: number;
      y?: number;
    } | undefined
  >(undefined);

  /* ========================================================================
     CURRENT TIME UPDATE
  ======================================================================== */

  useEffect(() => {
    const timer =
      setInterval(() => {
        setCurrentMinutes(
          getCurrentMinutes(),
        );
      }, 60 * 1000);

    return () => {
      clearInterval(timer);
    };
  }, []);

  /* ========================================================================
     LOGIN / LOGOUT
  ======================================================================== */

  const loginTime =
    formatTime(
      kpi?.loginTime,
    );

  const logoutTime =
    formatTime(
      kpi?.logoutTime,
    );

  const loginMinutes =
    timeToMinutes(
      kpi?.loginTime,
    );

  const logoutMinutes =
    timeToMinutes(
      kpi?.logoutTime,
    );

  const isActiveSession =
    loginMinutes !== null &&
    (
      logoutMinutes === null ||
      (
        currentMinutes >=
          loginMinutes &&
        currentMinutes <
          logoutMinutes
      )
    );

  /* ========================================================================
     CHART DATA

     API:
       05 = 4.16 + 31.18 + 15.38
       06 = 18.47 + 41.27 + 0.25
       07 = 25.18 + 31.25 + 3.53
       08 = 0 + 0 + 60
       09 = 43.53 + 8.57 + 7.68
       10 = 5.87 + 0 + 0
  ======================================================================== */

  const chartData =
    useMemo<ChartItem[]>(() => {
      if (
        !data?.hourly ||
        data.hourly.length === 0
      ) {
        return [];
      }

      const normalizedData: ChartItem[] =
        data.hourly
          .map((item) => {
            const hour =
              Number(
                item.hour,
              );

            const active =
              Number(
                item.activeMinutes ??
                  0,
              );

            const meeting =
              Number(
                item.meetingMinutes ??
                  0,
              );

            const idle =
              Number(
                item.idleMinutes ??
                  0,
              );

            const safeHour =
              Number.isFinite(
                hour,
              )
                ? hour
                : 0;

            const safeActive =
              Number.isFinite(
                active,
              ) &&
              active > 0
                ? active
                : 0;

            const safeMeeting =
              Number.isFinite(
                meeting,
              ) &&
              meeting > 0
                ? meeting
                : 0;

            const safeIdle =
              Number.isFinite(
                idle,
              ) &&
              idle > 0
                ? idle
                : 0;

            return {
              hour:
                safeHour,

              time:
                `${safeHour
                  .toString()
                  .padStart(
                    2,
                    '0',
                  )}:00`,

              active:
                safeActive,

              meeting:
                safeMeeting,

              idle:
                safeIdle,
            };
          })
          .sort(
            (a, b) =>
              a.hour -
              b.hour,
          );

      /* ----------------------------------------------------------------------
         FIRST NON-ZERO HOUR
      ---------------------------------------------------------------------- */

      const firstActiveIndex =
        normalizedData.findIndex(
          (item) =>
            item.active > 0 ||
            item.meeting > 0 ||
            item.idle > 0,
        );

      if (
        firstActiveIndex === -1
      ) {
        return [];
      }

      /* ----------------------------------------------------------------------
         LAST NON-ZERO HOUR
      ---------------------------------------------------------------------- */

      let lastActiveIndex =
        normalizedData.length -
        1;

      while (
        lastActiveIndex >= 0 &&
        normalizedData[
          lastActiveIndex
        ].active <= 0 &&
        normalizedData[
          lastActiveIndex
        ].meeting <= 0 &&
        normalizedData[
          lastActiveIndex
        ].idle <= 0
      ) {
        lastActiveIndex -= 1;
      }

      /*
       * Keep every hour between
       * first and last active hour.
       *
       * For your data:
       *
       * 05
       * 06
       * 07
       * 08
       * 09
       * 10
       */

      return normalizedData.slice(
        firstActiveIndex,
        lastActiveIndex + 1,
      );
    }, [data]);

  /* ========================================================================
     BAR SIZE

     For 6 visible bars:
       width = 38px
  ======================================================================== */

  const barSize =
    useMemo(() => {
      const count =
        chartData.length;

      if (count <= 0) {
        return 30;
      }

      if (count <= 5) {
        return 48;
      }

      if (count <= 8) {
        return 38;
      }

      if (count <= 12) {
        return 30;
      }

      if (count <= 16) {
        return 24;
      }

      if (count <= 20) {
        return 20;
      }

      return 17;
    }, [
      chartData.length,
    ]);

  /* ========================================================================
     X AXIS FONT SIZE
  ======================================================================== */

  const xAxisFontSize =
    useMemo(() => {
      const count =
        chartData.length;

      if (count <= 8) {
        return 11;
      }

      if (count <= 14) {
        return 10;
      }

      return 9;
    }, [
      chartData.length,
    ]);

  /* ========================================================================
     Y AXIS

     The API maximum is exactly 60 minutes.

     Therefore:
       0
       30
       60
  ======================================================================== */

  const maxTotal =
    Math.max(
      ...chartData.map(
        (item) =>
          item.active +
          item.meeting +
          item.idle,
      ),
      0,
    );

  /*
   * Prevent floating point
   * values such as:
   *
   * 59.999999
   *
   * from producing a strange
   * upper axis.
   */

  const roundedMaxTotal =
    Math.ceil(
      (maxTotal + 0.000001) /
        30,
    ) * 30;

  const yMax =
    Math.max(
      30,
      roundedMaxTotal,
    );

  const yTicks =
    Array.from(
      {
        length:
          yMax / 30 + 1,
      },
      (_, index) =>
        index * 30,
    );

  /* ========================================================================
     TOOLTIP POSITION
  ======================================================================== */

  const handleBarMouseEnter = (
    barData: any,
    index: number,
  ) => {
    if (index !== chartData.length - 1) {
      setTooltipPosition(undefined);
      return;
    }

    const barX = Number(barData?.x);

    if (!Number.isFinite(barX)) {
      setTooltipPosition(undefined);
      return;
    }

    // Move only the last-bar tooltip to the left.
    setTooltipPosition({
      x: Math.max(0, barX - 160),
    });
  };

  const handleBarMouseLeave = () => {
    setTooltipPosition(undefined);
  };

  /* ========================================================================
     LEGEND OPACITY
  ======================================================================== */

  const getOpacity = (
    activity: ActivityType,
  ): number => {
    if (!activeLegend) {
      return 1;
    }

    return activeLegend ===
      activity
      ? 1
      : 0.25;
  };

  /* ========================================================================
     CUSTOM TOOLTIP
  ======================================================================== */

  const CustomTooltip = ({
    active,
    payload,
  }: any) => {
    if (
      !active ||
      !payload ||
      !Array.isArray(
        payload,
      ) ||
      payload.length === 0
    ) {
      return null;
    }

    const hoveredPayload =
      payload[0];

    if (!hoveredPayload) {
      return null;
    }

    const dataKey =
      hoveredPayload.dataKey;

    const rawPayload =
      hoveredPayload.payload;

    if (!rawPayload) {
      return null;
    }

    /* ----------------------------------------------------------------------
       FIND ACTIVITY
    ---------------------------------------------------------------------- */

    let activity:
      | ActivityType
      | null = null;

    if (
      dataKey === 'active'
    ) {
      activity = 'active';
    } else if (
      dataKey === 'meeting'
    ) {
      activity = 'meeting';
    } else if (
      dataKey === 'idle'
    ) {
      activity = 'idle';
    }

    if (!activity) {
      return null;
    }

    /* ----------------------------------------------------------------------
       VALUE
    ---------------------------------------------------------------------- */

    const numericValue =
      Number(
        rawPayload[
          activity
        ] ?? 0,
      );

    const safeValue =
      Number.isFinite(
        numericValue,
      )
        ? numericValue
        : 0;

    if (safeValue <= 0) {
      return null;
    }

    /* ----------------------------------------------------------------------
       LABEL
    ---------------------------------------------------------------------- */

    const activityLabel =
      activity === 'active'
        ? 'Active'
        : activity === 'meeting'
          ? 'Meeting'
          : 'Idle';

    const activityColor =
      ACTIVITY_COLORS[
        activity
      ];

    /* ----------------------------------------------------------------------
       TIME
    ---------------------------------------------------------------------- */

    const tooltipTime =
      formatHourRange(
        rawPayload.hour,
      );

    /* ----------------------------------------------------------------------
       TOOLTIP
    ---------------------------------------------------------------------- */

    return (
      <div
        className="
          min-w-[150px]
          overflow-hidden
          rounded-[6px]
          border
          border-[#D0D5DD]
          bg-white
          shadow-[0_4px_12px_rgba(16,24,40,0.15)]
        "
      >
        <div
          className="
            border-b
            border-[#EAECF0]
            bg-[#F8F9FB]
            px-3
            py-2
            text-[12px]
            font-medium
            text-[#344054]
          "
        >
          {tooltipTime}
        </div>

        <div
          className="
            flex
            items-center
            justify-between
            gap-6
            px-3
            py-2.5
            text-[12px]
          "
        >
          <div
            className="
              flex
              items-center
              gap-2
            "
          >
            <span
              className="
                h-3
                w-3
                shrink-0
                rounded-full
              "
              style={{
                backgroundColor:
                  activityColor,
              }}
            />

            <span
              className="
                text-[#344054]
              "
            >
              {activityLabel}
            </span>
          </div>

          <span
            className="
              font-semibold
              text-[#111827]
            "
          >
            {safeValue.toFixed(
              1,
            )}
            m
          </span>
        </div>
      </div>
    );
  };

  /* ========================================================================
     LEGEND
  ======================================================================== */

  const legendItems: {
    type: ActivityType;
    label: string;
    color: string;
  }[] = [
    {
      type: 'active',
      label: 'Active',
      color:
        ACTIVITY_COLORS.active,
    },
    {
      type: 'meeting',
      label: 'Meeting',
      color:
        ACTIVITY_COLORS.meeting,
    },
    {
      type: 'idle',
      label: 'Idle',
      color:
        ACTIVITY_COLORS.idle,
    },
  ];

  /* ========================================================================
     RENDER
  ======================================================================== */

  return (
    <div
      className="
        w-full
        min-w-0
        rounded-[18px]
        border
        border-[#D0D5DD]
        bg-white
        p-[18px]
      "
    >
      {/* ====================================================================
          HEADER
      ==================================================================== */}

      <div
        className="
          mb-[14px]
          flex
          w-full
          min-w-0
          flex-col
          gap-[10px]
          xl:flex-row
          xl:items-center
          xl:gap-[10px]
        "
      >
        <h3
          className="
            shrink-0
            text-[14px]
            font-semibold
            leading-[20px]
            text-[#002B5B]
          "
        >
          Today's Hour-by-Hour Log
        </h3>

        <div
          className="
            flex
            min-w-0
            max-w-full
            flex-wrap
            items-center
            gap-x-[6px]
            gap-y-[2px]
            rounded-full
            bg-[#EEF6FF]
            px-[10px]
            py-[5px]
            text-[10px]
            font-medium
            leading-[18px]
            xl:h-[28px]
            xl:flex-nowrap
            xl:py-0
          "
        >
          <span
            className="
              shrink-0
              whitespace-nowrap
              text-[#0070C0]
            "
          >
            Login {loginTime}
          </span>

          <span
            className="
              shrink-0
              text-[#98A2B3]
            "
          >
            ·
          </span>

          <span
            className={`
              shrink-0
              whitespace-nowrap
              ${
                isActiveSession
                  ? 'font-semibold text-[#007A4D]'
                  : 'font-medium text-[#667085]'
              }
            `}
          >
            {isActiveSession
              ? 'Active session running'
              : 'Session completed'}
          </span>

          <span
            className="
              shrink-0
              text-[#98A2B3]
            "
          >
            ·
          </span>

          <span
            className="
              shrink-0
              whitespace-nowrap
              text-[#0070C0]
            "
          >
            {kpi?.logoutTime
              ? `Logout ${logoutTime}`
              : 'Expected logout --:--'}
          </span>
        </div>
      </div>

      {/* ====================================================================
          LEGEND
      ==================================================================== */}

      <div
        className="
          mb-4
          flex
          flex-wrap
          items-center
          justify-center
          gap-x-5
          gap-y-2
          text-[12px]
        "
      >
        {legendItems.map(
          (item) => (
            <div
              key={
                item.type
              }
              className="
                flex
                cursor-pointer
                items-center
                gap-1
                whitespace-nowrap
              "
              onMouseEnter={() =>
                setActiveLegend(
                  item.type,
                )
              }
              onMouseLeave={() =>
                setActiveLegend(
                  null,
                )
              }
            >
              <span
                className="
                  h-3
                  w-3
                  rounded-sm
                "
                style={{
                  backgroundColor:
                    item.color,
                  opacity:
                    getOpacity(
                      item.type,
                    ),
                }}
              />

              <span
                style={{
                  opacity:
                    getOpacity(
                      item.type,
                    ),
                }}
              >
                {item.label}
              </span>
            </div>
          ),
        )}
      </div>

      {/* ====================================================================
          CHART
      ==================================================================== */}

      <div
        className="
          w-full
          min-w-0
          overflow-visible
        "
      >
        <ResponsiveContainer
          width="100%"
          height={280}
        >
          <BarChart
            data={chartData}
            margin={{
              top: 12,
              right: 15,
              left: 10,
              bottom: 25,
            }}
            barCategoryGap="22%"
          >
            {/* ================================================================
                GRID
            ================================================================= */}

            <CartesianGrid
              vertical={false}
              stroke="#EAEAEA"
            />

            {/* ================================================================
                X AXIS
            ================================================================= */}

            <XAxis
              dataKey="time"
              axisLine={false}
              tickLine={false}
              interval={0}
              tickMargin={8}
              height={30}
              tick={{
                fontSize:
                  xAxisFontSize,
                fill: '#667085',
              }}
            />

            {/* ================================================================
                Y AXIS
            ================================================================= */}

            <YAxis
              domain={[
                0,
                yMax,
              ]}
              ticks={yTicks}
              axisLine={false}
              tickLine={false}
              width={42}
              tick={{
                fontSize: 10,
                fill: '#667085',
              }}
              tickFormatter={(
                value,
              ) =>
                `${value} m`
              }
            />

            {/* ================================================================
                TOOLTIP

                shared=false ensures the hovered
                stacked segment is shown.
            ================================================================= */}

            <Tooltip
              shared={false}
              content={
                <CustomTooltip />
              }
              cursor={{
                fill: 'transparent',
              }}
              allowEscapeViewBox={{
                x: true,
                y: true,
              }}
              position={
                tooltipPosition
              }
              wrapperStyle={{
                zIndex: 99999,
                pointerEvents:
                  'none',
                overflow:
                  'visible',
                maxWidth:
                  'calc(100vw - 16px)',
              }}
            />

            {/* ================================================================
                ACTIVE

                Custom shape makes the Active segment
                rounded ONLY when Active is the top
                segment.

                Example 10:00:
                    ┌───────┐
                    │Active │
                    └───────┘
            ================================================================= */}

            <Bar
              dataKey="active"
              name="Active"
              stackId="activity"
              fill={
                ACTIVITY_COLORS.active
              }
              fillOpacity={getOpacity(
                'active',
              )}
              maxBarSize={
                barSize
              }
              shape={
                <TopRoundedBar />
              }
              onMouseEnter={
                handleBarMouseEnter
              }
              onMouseLeave={
                handleBarMouseLeave
              }
            />

            {/* ================================================================
                MEETING

                Meeting gets rounded only if it is
                actually the top segment.
            ================================================================= */}

            <Bar
              dataKey="meeting"
              name="Meeting"
              stackId="activity"
              fill={
                ACTIVITY_COLORS.meeting
              }
              fillOpacity={getOpacity(
                'meeting',
              )}
              maxBarSize={
                barSize
              }
              shape={
                <TopRoundedBar />
              }
              onMouseEnter={
                handleBarMouseEnter
              }
              onMouseLeave={
                handleBarMouseLeave
              }
            />

            {/* ================================================================
                IDLE

                Idle gets rounded when it is the top
                segment.

                For your data:
                    05 -> Idle top
                    06 -> Idle top
                    07 -> Idle top
                    08 -> Idle only
                    09 -> Idle top
                    10 -> Idle zero, so Active gets top
            ================================================================= */}

            <Bar
              dataKey="idle"
              name="Idle"
              stackId="activity"
              fill={
                ACTIVITY_COLORS.idle
              }
              fillOpacity={getOpacity(
                'idle',
              )}
              maxBarSize={
                barSize
              }
              shape={
                <TopRoundedBar />
              }
              onMouseEnter={
                handleBarMouseEnter
              }
              onMouseLeave={
                handleBarMouseLeave
              }
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* ====================================================================
          FOOTER
      ==================================================================== */}

      <div
        className="
          mt-4
          flex
          flex-wrap
          items-center
          gap-x-6
          gap-y-3
          text-[12px]
        "
      >
        {/* ACTIVE */}

        <div
          className="
            flex
            items-center
            gap-2
            whitespace-nowrap
          "
        >
          <span
            className="
              h-3
              w-3
              rounded
              bg-[#4CC66F]
            "
          />

          <span>
            Active
          </span>

          <span
            className="
              text-[#667085]
            "
          >
            {summaryValue(
              data?.summary
                ?.activeTotal,
            )}
          </span>
        </div>

        {/* MEETING */}

        <div
          className="
            flex
            items-center
            gap-2
            whitespace-nowrap
          "
        >
          <span
            className="
              h-3
              w-3
              rounded
              bg-[#5A8DEE]
            "
          />

          <span>
            Meeting
          </span>

          <span
            className="
              text-[#667085]
            "
          >
            {summaryValue(
              data?.summary
                ?.meetingTotal,
            )}
          </span>
        </div>

        {/* IDLE */}

        <div
          className="
            flex
            items-center
            gap-2
            whitespace-nowrap
          "
        >
          <span
            className="
              h-3
              w-3
              rounded
              bg-[#F3AD2B]
            "
          />

          <span>
            Idle
          </span>

          <span
            className="
              text-[#667085]
            "
          >
            {summaryValue(
              data?.summary
                ?.idleTotal,
            )}
          </span>
        </div>
      </div>
    </div>
  );
};

export default HourlyActivityLog;